import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from "react-helmet-async";
import { RowsPhotoAlbum } from 'react-photo-album';
import Lightbox from 'yet-another-react-lightbox';

import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

import 'react-photo-album/rows.css';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import '../css/PhotoGallery.css';

const SITE = "https://www.dysband.com";

export default function GalleryWithLightbox() {
    const [index, setIndex] = useState(-1);
    const [galleryData, setGalleryData] = useState([]);
    const [loading, setLoading] = useState(true);

    // ✅ Load gallery JSON from PUBLIC folder
    useEffect(() => {
        fetch('/json/galleryData.json')
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to load gallery data');
                }

                return res.json();
            })
            .then((data) => {
                setGalleryData(data);
            })
            .catch((err) => {
                console.error('Gallery JSON Error:', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // ✅ Normalize + protect against malformed data
    const photos = useMemo(() => {
        return galleryData
            .filter(img => img?.src && img?.thumb)
            .map((img) => ({
                src: img.thumb.startsWith('/')
                    ? img.thumb
                    : `/${img.thumb}`,

                original: img.src.startsWith('/')
                    ? img.src
                    : `/${img.src}`,

                width: img.width || 500,
                height: img.height || 300,
                alt: img.alt || '',
            }));
    }, [galleryData]);

    if (loading) {
        return (
            <div className="gallery">
                <h3>Gallery</h3>
                <p>Loading gallery...</p>
            </div>
        );
    }

    return (
        <div className="gallery">
            <h3>Gallery</h3>

            {/*
              NOTE: this component used to set the page <title>, description and og: tags
              from the current photo. It is used inside other pages (/yacht-rock-band,
              /BioPage), so that overrode their own SEO tags. Page tags now belong to the
              page components only.
            */}

            {/* ✅ Structured Data */}
            {photos.length > 0 && (
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "ImageGallery",
                            "name": "Dirty Yachtin' Scoundrels Gallery",
                            "image": photos.map((p) => ({
                                "@type": "ImageObject",
                                "contentUrl": `${SITE}${p.original}`,
                                "description": p.alt
                            }))
                        })}
                    </script>
                </Helmet>
            )}

            {/* ✅ Gallery */}
            <RowsPhotoAlbum
                photos={photos}
                targetRowHeight={150}
                onClick={({ index }) => setIndex(index)}
                renderPhoto={({ photo }) => (
                    <div
                        style={{
                            width: '100%',
                            padding: '0.25rem'
                        }}
                    >
                        <img
                            src={photo.src}
                            alt={photo.alt}
                            loading="lazy"
                            className="photo-thumb"
                            style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                            }}
                            onError={(e) => {
                                console.error(
                                    'Image failed:',
                                    photo.src
                                );

                                // Hide broken image
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                )}
            />

            {/* ✅ Lightbox */}
            <Lightbox
                slides={photos.map(({ original, alt }) => ({
                    src: original,
                    alt
                }))}
                open={index >= 0}
                index={index}
                close={() => setIndex(-1)}
                plugins={[
                    Fullscreen,
                    Slideshow,
                    Thumbnails,
                    Zoom
                ]}
            />
        </div>
    );
}